import {
  KmxLeafFilled,
  KmxLeafStroked,
  KmxLightningBoltFilled,
  KmxLightningBoltStroked,
  KmxPlugFilled,
  KmxPlugStroked,
} from '@kmx/mui-icons';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { useMemo, useState } from 'react';

import DialogTitleWithCloseButton from '@/components/DialogTitleWithCloseButton/DialogTitleWithCloseButton';
import ENGINETYPE from '@/constants/engineTypeConstants';

type ElectricVehicleInfoProps = {
  carEngineType: string;
};

const ModalSectionHeading = styled(Typography)<TypographyProps>({
  display: 'flex',
  alignItems: 'center',
});

const ElectricVehicleInfo = ({ carEngineType }: ElectricVehicleInfoProps) => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const iconStroked = useMemo(() => {
    switch (carEngineType.toLowerCase()) {
      case ENGINETYPE.electric:
        return <KmxLightningBoltStroked />;
      case ENGINETYPE.hybrid:
        return <KmxLeafStroked />;
      case ENGINETYPE.plugin:
        return <KmxPlugStroked />;
      default:
        return null;
    }
  }, [carEngineType]);

  return (
    <>
      <IconButton
        color="success"
        onClick={handleClickOpen}
        className="kmx-car-tile__electric-vehicle-info-modal-trigger"
      >
        {iconStroked}
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        data-is-modal="true"
        className="kmx-car-tile__electric-vehicle-info-modal"
      >
        <DialogTitleWithCloseButton variant="h5" onClose={handleClose}>
          Electric Vehicle Types
        </DialogTitleWithCloseButton>

        <DialogContent>
          <ModalSectionHeading variant="h6" component="h2">
            <KmxLightningBoltFilled color="success" />
            Electric
          </ModalSectionHeading>
          <Typography marginBottom={2}>
            A battery and electric motor are used for propulsion instead of a
            conventional internal combustion engine (ICE).
          </Typography>

          <ModalSectionHeading variant="h6" component="h2">
            <KmxLeafFilled color="success" />
            Hybrid
          </ModalSectionHeading>
          <Typography marginBottom={2}>
            A conventional internal combustion engine (ICE) powered by gasoline
            combined with a supplemental battery that doesn&apos;t need to be
            plugged in.
          </Typography>

          <ModalSectionHeading variant="h6" component="h2">
            <KmxPlugFilled color="success" />
            Plug-in hybrid
          </ModalSectionHeading>
          <Typography marginBottom={2}>
            A conventional internal combustion engine (ICE) powered by gasoline
            combined with a supplemental battery that can be plugged in to
            recharge.
          </Typography>

          <Button
            variant="contained"
            color="cta"
            href="/research/powertrains-and-fuel-types"
          >
            Explore Fuel Types
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ElectricVehicleInfo;
